import { useEffect, useState } from "react";
import { Plus, Trash2, Pencil, X, Tags, ChevronUp, ChevronDown, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { usePortal } from "@/context/PortalContext";
import { term } from "@/lib/roles";
import { Card, Btn, Loader } from "@/components/portal/ui";

const LANGS = ["de", "fr", "it", "en"];
const emptyI18n = { de: "", fr: "", it: "", en: "" };
const emptyForm = {
  slug: "", name: { ...emptyI18n }, icon: "", description: { ...emptyI18n },
  meta_title: { ...emptyI18n }, meta_description: { ...emptyI18n }, active: true, visible: true,
};

function Switch({ on, onClick, testid, disabled }) {
  return (
    <button data-testid={testid} onClick={onClick} disabled={disabled} aria-pressed={on} type="button"
      className="relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors disabled:opacity-50"
      style={{ background: on ? "var(--accent)" : "var(--surface-alt)" }}>
      <span className="inline-block h-4 w-4 rounded-full bg-white shadow transition-transform"
        style={{ transform: on ? "translateX(24px)" : "translateX(4px)" }} />
    </button>
  );
}

function I18nField({ label, value, onChange, textarea, testid }) {
  const st = { background: "var(--bg)", borderColor: "var(--border)", borderRadius: "var(--radius)", color: "var(--text)" };
  return (
    <div>
      <label className="text-xs font-semibold uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>{label}</label>
      <div className="grid grid-cols-2 gap-2 mt-1">
        {LANGS.map((lng) => (
          <div key={lng} className="relative">
            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[10px] font-bold uppercase" style={{ color: "var(--text-muted)" }}>{lng}</span>
            {textarea ? (
              <textarea data-testid={`${testid}-${lng}`} rows={2} className="w-full pl-8 pr-2 py-2 text-sm border outline-none" style={st}
                value={value?.[lng] || ""} onChange={(e) => onChange({ ...value, [lng]: e.target.value })} />
            ) : (
              <input data-testid={`${testid}-${lng}`} className="w-full pl-8 pr-2 h-10 text-sm border outline-none" style={st}
                value={value?.[lng] || ""} onChange={(e) => onChange({ ...value, [lng]: e.target.value })} />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export function CategoriesManager() {
  const { t, L } = useI18n();
  const { config } = usePortal();
  const catSingular = term(config, "category", L, L({ de: "Kategorie", fr: "Catégorie", it: "Categoria", en: "Category" }));
  const catPlural = term(config, "categories", L, L({ de: "Kategorien", fr: "Catégories", it: "Categorie", en: "Categories" }));
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get("/admin/categories");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm({ ...emptyForm, name: { ...emptyI18n }, description: { ...emptyI18n }, meta_title: { ...emptyI18n }, meta_description: { ...emptyI18n } }); setEditing("new"); };
  const openEdit = (c) => {
    setForm({
      slug: c.slug || "", name: { ...emptyI18n, ...(c.name || {}) }, icon: c.icon || "",
      description: { ...emptyI18n, ...(c.description || {}) },
      meta_title: { ...emptyI18n, ...(c.meta_title || {}) },
      meta_description: { ...emptyI18n, ...(c.meta_description || {}) },
      active: c.active !== false, visible: c.visible !== false,
    });
    setEditing(c.id);
  };
  const close = () => { setEditing(null); setForm(emptyForm); };

  const save = async () => {
    setSaving(true);
    try {
      if (editing === "new") await api.post("/admin/categories", form);
      else await api.put(`/admin/categories/${editing}`, form);
      toast.success(t("common.saved"));
      close();
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const quickToggle = async (c, field) => {
    try {
      await api.put(`/admin/categories/${c.id}`, {
        slug: c.slug, name: c.name, icon: c.icon, description: c.description,
        meta_title: c.meta_title, meta_description: c.meta_description,
        active: field === "active" ? !(c.active !== false) : c.active !== false,
        visible: field === "visible" ? !(c.visible !== false) : c.visible !== false,
      });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const move = async (idx, dir) => {
    const arr = [...items];
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    setItems(arr);
    try { await api.post("/admin/categories/reorder", { ids: arr.map((c) => c.id) }); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); load(); }
  };

  const remove = async (c) => {
    if (!window.confirm(L({ de: "Kategorie wirklich löschen?", fr: "Supprimer la catégorie ?", it: "Eliminare la categoria?", en: "Delete this category?" }))) return;
    try {
      const { data } = await api.delete(`/admin/categories/${c.id}`);
      if (data.in_use) toast.warning(L({ de: `Gelöscht – war ${data.in_use} Anbietern zugewiesen.`, fr: `Supprimé – assignée à ${data.in_use} prestataires.`, it: `Eliminata – assegnata a ${data.in_use} fornitori.`, en: `Deleted – was assigned to ${data.in_use} providers.` }));
      else toast.success(t("common.saved"));
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const st = { background: "var(--bg)", borderColor: "var(--border)", borderRadius: "var(--radius)", color: "var(--text)" };
  if (!items) return <Loader />;

  return (
    <div className="space-y-4" data-testid="categories-manager">
      <div className="flex items-center justify-between">
        <p className="text-sm" style={{ color: "var(--text-muted)" }}>{items.length} · {catPlural}</p>
        <Btn onClick={openNew} data-testid="cat-new-btn"><Plus className="h-4 w-4" /> {catSingular}</Btn>
      </div>

      {items.length === 0 && (
        <Card className="p-12 flex flex-col items-center gap-3 text-center" data-testid="cat-empty">
          <Tags className="h-8 w-8" style={{ color: "var(--accent)" }} />
          <span style={{ color: "var(--text-muted)" }}>{L({ de: "Noch keine", fr: "Aucune", it: "Nessuna", en: "No" })} {catPlural.toLowerCase()}.</span>
        </Card>
      )}

      <div className="space-y-2">
        {items.map((c, idx) => (
          <Card key={c.id} className="p-3 flex flex-wrap items-center gap-3 kw-fade-up" data-testid={`cat-item-${c.id}`}>
            <div className="flex flex-col">
              <button onClick={() => move(idx, -1)} disabled={idx === 0} className="disabled:opacity-30" data-testid={`cat-up-${c.id}`}><ChevronUp className="h-4 w-4" style={{ color: "var(--text-muted)" }} /></button>
              <button onClick={() => move(idx, 1)} disabled={idx === items.length - 1} className="disabled:opacity-30" data-testid={`cat-down-${c.id}`}><ChevronDown className="h-4 w-4" style={{ color: "var(--text-muted)" }} /></button>
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-medium truncate flex items-center gap-2" style={{ color: "var(--text)" }}>
                {L(c.name) || c.slug}
                {c.active === false && <span className="text-[10px] uppercase px-1.5 py-0.5 rounded-full" style={{ background: "var(--surface-alt)", color: "var(--text-muted)" }}>{L({ de: "inaktiv", fr: "inactif", it: "inattivo", en: "inactive" })}</span>}
              </div>
              <div className="text-xs truncate" style={{ color: "var(--text-muted)" }}>/{c.slug}{c.icon ? ` · ${c.icon}` : ""}</div>
            </div>

            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1.5" title={L({ de: "Aktiv", fr: "Actif", it: "Attivo", en: "Active" })}>
                <span className="text-[10px] uppercase" style={{ color: "var(--text-muted)" }}>{L({ de: "Aktiv", fr: "Actif", it: "Attivo", en: "Active" })}</span>
                <Switch testid={`cat-active-${c.id}`} on={c.active !== false} onClick={() => quickToggle(c, "active")} />
              </div>
              <button onClick={() => quickToggle(c, "visible")} data-testid={`cat-visible-${c.id}`} title={L({ de: "Sichtbarkeit", fr: "Visibilité", it: "Visibilità", en: "Visibility" })}
                className="h-9 w-9 flex items-center justify-center" style={{ borderRadius: "var(--radius)", background: "var(--surface-alt)" }}>
                {c.visible !== false ? <Eye className="h-4 w-4" style={{ color: "var(--accent)" }} /> : <EyeOff className="h-4 w-4" style={{ color: "var(--text-muted)" }} />}
              </button>
              <Btn variant="soft" onClick={() => openEdit(c)} className="h-9 px-3 text-xs" data-testid={`cat-edit-${c.id}`}><Pencil className="h-3.5 w-3.5" /></Btn>
              <Btn variant="danger" onClick={() => remove(c)} className="h-9 px-3" data-testid={`cat-delete-${c.id}`}><Trash2 className="h-3.5 w-3.5" /></Btn>
            </div>
          </Card>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,.5)" }} data-testid="cat-modal">
          <Card className="w-full max-w-2xl p-6 max-h-[90vh] overflow-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-xl font-bold flex items-center gap-2" style={{ color: "var(--text)" }}><Tags className="h-5 w-5" style={{ color: "var(--accent)" }} /> {editing === "new" ? L({ de: "Neue Kategorie", fr: "Nouvelle catégorie", it: "Nuova categoria", en: "New category" }) : L({ de: "Kategorie bearbeiten", fr: "Modifier la catégorie", it: "Modifica categoria", en: "Edit category" })}</h3>
              <button onClick={close} data-testid="cat-modal-close"><X className="h-5 w-5" style={{ color: "var(--text-muted)" }} /></button>
            </div>
            <div className="space-y-4">
              <I18nField testid="cat-name" label={L({ de: "Name", fr: "Nom", it: "Nome", en: "Name" })} value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>Icon (lucide)</label>
                  <input data-testid="cat-icon" placeholder="scissors" className="w-full px-3.5 h-10 text-sm border outline-none mt-1" style={st}
                    value={form.icon} onChange={(e) => setForm({ ...form, icon: e.target.value })} />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>Slug</label>
                  <input data-testid="cat-slug" placeholder="auto" className="w-full px-3.5 h-10 text-sm border outline-none mt-1" style={st}
                    value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} />
                </div>
              </div>
              <I18nField testid="cat-desc" label={L({ de: "Beschreibung", fr: "Description", it: "Descrizione", en: "Description" })} value={form.description} onChange={(v) => setForm({ ...form, description: v })} textarea />
              <div className="pt-2 text-xs font-semibold uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>SEO</div>
              <I18nField testid="cat-metatitle" label="Meta Title" value={form.meta_title} onChange={(v) => setForm({ ...form, meta_title: v })} />
              <I18nField testid="cat-metadesc" label="Meta Description" value={form.meta_description} onChange={(v) => setForm({ ...form, meta_description: v })} textarea />
              <div className="flex items-center gap-6 pt-1">
                <div className="flex items-center gap-2"><span className="text-sm" style={{ color: "var(--text)" }}>{L({ de: "Aktiv", fr: "Actif", it: "Attivo", en: "Active" })}</span><Switch testid="cat-form-active" on={form.active} onClick={() => setForm({ ...form, active: !form.active })} /></div>
                <div className="flex items-center gap-2"><span className="text-sm" style={{ color: "var(--text)" }}>{L({ de: "Sichtbar", fr: "Visible", it: "Visibile", en: "Visible" })}</span><Switch testid="cat-form-visible" on={form.visible} onClick={() => setForm({ ...form, visible: !form.visible })} /></div>
              </div>
              <Btn onClick={save} disabled={saving || !form.name.de} className="w-full" data-testid="cat-save-btn">{t("common.save")}</Btn>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
